import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useUtilityGeneratePfx } from "@/hooks/queries";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Field, Input, Textarea } from "@/components/ui/Input";
import { base64ToBytes, downloadFile, formatDate } from "@/lib/utils";
import type { UtilityPfxInput, UtilityPfxResponse } from "@/types";

export function UtilitiesPfx() {
  const [form, setForm] = useState<UtilityPfxInput>({
    key: "",
    pem: "",
    password: "",
  });
  const [chain, setChain] = useState<string[]>([]);
  const [result, setResult] = useState<UtilityPfxResponse | null>(null);
  const mutation = useUtilityGeneratePfx();

  function set<K extends keyof UtilityPfxInput>(key: K, value: UtilityPfxInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const res = await mutation.mutateAsync({
        ...form,
        chain: chain.length > 0 ? chain : undefined,
      });
      setResult(res);
      toast.success("PFX generated");
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to generate PFX");
    }
  }

  function download(result: UtilityPfxResponse) {
    downloadFile(result.pfxFileName, base64ToBytes(result.pfx), result.pfxContentType);
    toast.success(`Downloaded ${result.pfxFileName}`);
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Utilities · Generate PFX</h1>
        <p className="text-sm text-slate-500 mt-1">
          Stateless PFX generation from private key + signed PEM. Known Andes SCD issuers get an
          automatic CA chain.
        </p>
      </div>

      <form onSubmit={onSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Inputs</CardTitle>
            <CardDescription>Provide PEM-encoded private key and signed certificate.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Private key (PEM)" required>
              <Textarea
                rows={8}
                value={form.key}
                onChange={(e) => set("key", e.target.value)}
                placeholder="-----BEGIN PRIVATE KEY-----…"
                required
                maxLength={16_000}
              />
            </Field>
            <Field label="Signed certificate (PEM)" required>
              <Textarea
                rows={8}
                value={form.pem}
                onChange={(e) => set("pem", e.target.value)}
                placeholder="-----BEGIN CERTIFICATE-----…"
                required
                maxLength={16_000}
              />
            </Field>
            <Field label="PFX password" required hint="Used to encrypt the resulting PFX">
              <Input
                type="text"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                required
                maxLength={256}
              />
            </Field>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-slate-700">
                  CA chain (optional, up to 10)
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setChain((c) => (c.length < 10 ? [...c, ""] : c))}
                  disabled={chain.length >= 10}
                >
                  <Plus className="h-3.5 w-3.5" /> Add intermediate
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mb-2">
                Required if the issuer is not a known Andes SCD CA.
              </p>
              <div className="space-y-2">
                {chain.map((cert, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Textarea
                      rows={4}
                      value={cert}
                      onChange={(e) =>
                        setChain((c) => c.map((v, i) => (i === idx ? e.target.value : v)))
                      }
                      placeholder={`Intermediate CA ${idx + 1} (PEM)`}
                      maxLength={16_000}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setChain((c) => c.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="mt-3">
          <Button type="submit" loading={mutation.isPending}>
            Generate PFX
          </Button>
        </div>
      </form>

      {result && (
        <Card>
          <CardHeader>
            <CardTitle>Result</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-slate-400">Friendly name</p>
                <p className="text-slate-900">{result.friendlyName}</p>
              </div>
              <div>
                <p className="text-slate-400">Serial</p>
                <p className="text-slate-900 font-mono break-all">{result.serialNumber}</p>
              </div>
              <div>
                <p className="text-slate-400">Issuer</p>
                <p className="text-slate-900">{result.issuer}</p>
              </div>
              <div>
                <p className="text-slate-400">Subject</p>
                <p className="text-slate-900 break-all">{result.subject}</p>
              </div>
              <div>
                <p className="text-slate-400">Valid from</p>
                <p className="text-slate-900">{formatDate(result.validFrom)}</p>
              </div>
              <div>
                <p className="text-slate-400">Valid to</p>
                <p className="text-slate-900">{formatDate(result.validTo)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => download(result)}>
                Download {result.pfxFileName}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
